/**
 * US 13.1 / 13.2 / 13.3 — what Mochi would create or change, before anything
 * happens. Every understood field is shown and can be changed; each item can
 * be unticked; a name with several matches must be chosen. Confirming applies
 * the ticked items as one batch, and the toast offers Undo for all of it.
 * Written in the language of the request, right-to-left for Arabic.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Sparkles } from "lucide-react";
import { cn } from "@weco/ui";
import { mochiApi, type ApplyResult, type ChangeItem, type CreateItem, type Proposal, type SquadBrief } from "./api";

const PRIORITIES = ["urgent", "high", "medium", "low"] as const;

function useCardT(lang: string) {
  const { i18n } = useTranslation();
  return useMemo(() => i18n.getFixedT(lang === "ar" ? "ar" : "en"), [i18n, lang]);
}

function CreateRow({ item, squads, edit, onEdit, t }: { item: CreateItem; squads: SquadBrief[]; edit: Partial<CreateItem>; onEdit: (p: Partial<CreateItem>) => void; t: (k: string, o?: Record<string, unknown>) => string }) {
  const v = { ...item, ...edit };
  const members = v.squad?.members ?? [];
  const cls = "h-9 px-2.5 rounded-xl border border-border bg-background text-xs";
  return (
    <div className="space-y-2">
      <input value={v.title} onChange={(e) => onEdit({ title: e.target.value })} aria-label={t("tasks.mochi.title", { defaultValue: "Title" })} className="w-full h-9 px-2.5 rounded-xl border border-border bg-background text-sm font-semibold" />
      {v.quote && <p className="text-[11px] text-muted-foreground italic line-clamp-2">“{v.quote}”</p>}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={v.private ? "__private" : (v.squad?.id ?? "")}
          onChange={(e) => {
            const id = e.target.value;
            if (id === "__private") onEdit({ private: true, squad: null, needs_squad: false });
            else onEdit({ private: false, squad: squads.find((s) => s.id === id) ?? null, needs_squad: !id });
          }}
          aria-label={t("tasks.mochi.squad", { defaultValue: "Squad" })}
          className={cn(cls, v.needs_squad && !v.squad && !v.private && "border-amber-500")}
        >
          <option value="">{t("tasks.hubs.newTask.pickSquad", { defaultValue: "Choose a squad" })}</option>
          {squads.map((s) => (
            <option key={s.id} value={s.id}>
              {s.hub_name} / {s.name}
            </option>
          ))}
          <option value="__private">{t("tasks.mochi.private", { defaultValue: "Private (only you)" })}</option>
        </select>
        <select value={v.priority} onChange={(e) => onEdit({ priority: e.target.value })} aria-label={t("tasks.mochi.priority", { defaultValue: "Priority" })} className={cls}>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {t(`tasks.priority.${p}`, { defaultValue: p })}
            </option>
          ))}
        </select>
        <input type="date" value={v.due_date ?? ""} onChange={(e) => onEdit({ due_date: e.target.value || null })} aria-label={t("tasks.fields.dueDate", { defaultValue: "Due date" })} className={cls} />
        <input type="time" value={v.due_time ?? ""} onChange={(e) => onEdit({ due_time: e.target.value || null })} aria-label={t("tasks.mochi.dueTime", { defaultValue: "Due time" })} className={cls} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">{t("tasks.mochi.assignees", { defaultValue: "Assignees" })}:</span>
        {v.assignees.map((a) => (
          <span key={a.email} className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-muted">
            {a.name}
            <button type="button" onClick={() => onEdit({ assignees: v.assignees.filter((x) => x.email !== a.email) })} aria-label={t("tasks.mochi.removePerson", { name: a.name, defaultValue: "Remove {{name}}" })}>
              ×
            </button>
          </span>
        ))}
        {Object.entries(v.ambiguous ?? {}).map(([name, choices]) => (
          <select
            key={name}
            value=""
            onChange={(e) => {
              const p = choices.find((c) => c.email === e.target.value);
              if (!p) return;
              const rest = { ...v.ambiguous };
              delete rest[name];
              onEdit({ assignees: [...v.assignees, p], ambiguous: rest });
            }}
            aria-label={t("tasks.mochi.whichPerson", { name, defaultValue: "Which {{name}}?" })}
            className={cn(cls, "border-amber-500")}
          >
            <option value="">{t("tasks.mochi.whichPerson", { name, defaultValue: "Which {{name}}?" })}</option>
            {choices.map((c) => (
              <option key={c.email} value={c.email}>
                {c.name} · {c.email}
              </option>
            ))}
          </select>
        ))}
        {members.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              const p = members.find((m) => m.email === e.target.value);
              if (p && !v.assignees.some((a) => a.email === p.email)) onEdit({ assignees: [...v.assignees, p] });
            }}
            aria-label={t("tasks.mochi.addPerson", { defaultValue: "Add someone" })}
            className={cls}
          >
            <option value="">+ {t("tasks.mochi.addPerson", { defaultValue: "Add someone" })}</option>
            {members.map((m) => (
              <option key={m.email} value={m.email}>
                {m.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {(v.labels.length > 0 || v.repeat_rule || v.remind_at || v.category) && (
        <p className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3">
          {v.category && <span>{t("tasks.mochi.category", { defaultValue: "Category" })}: {v.category}</span>}
          {v.labels.length > 0 && <span>{t("tasks.mochi.labels", { defaultValue: "Labels" })}: {v.labels.join(", ")}</span>}
          {v.repeat_rule && <span>{t("tasks.mochi.repeat", { defaultValue: "Repeats" })}: {v.repeat_rule.kind}</span>}
          {v.remind_at && <span>{t("tasks.mochi.reminder", { defaultValue: "Reminder" })}: {new Date(v.remind_at).toLocaleString()}</span>}
        </p>
      )}
    </div>
  );
}

function fieldLabel(field: string, t: (k: string, o?: Record<string, unknown>) => string) {
  return t(`tasks.mochi.field.${field}`, { defaultValue: field });
}

export function ProposalCard({ proposal, onOpenTask }: { proposal: Proposal; onOpenTask?: (id: string) => void }) {
  const t = useCardT(proposal.lang);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const openTask = onOpenTask ?? ((id: string) => navigate(`/tasks/t/${id}`));
  const [ticked, setTicked] = useState<boolean[]>(() => proposal.items.map(() => true));
  const [edits, setEdits] = useState<Record<string, Partial<CreateItem>>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const create = proposal.kind === "create";
  const chosen = ticked.filter(Boolean).length;
  const unresolved = create
    ? proposal.items.some((it, i) => {
        if (!ticked[i]) return false;
        const v = { ...(it as CreateItem), ...edits[String(i)] };
        return Object.keys(v.ambiguous ?? {}).length > 0 || (v.needs_squad && !v.squad && !v.private) || !v.title.trim();
      })
    : false;

  const confirmLabel = create
    ? t("tasks.mochi.createN", { count: chosen, defaultValue: "Create {{count}} tasks" })
    : proposal.action === "delete"
      ? t("tasks.mochi.deleteN", { count: chosen, defaultValue: "Delete {{count}} tasks" })
      : proposal.action === "archive"
        ? t("tasks.mochi.archiveN", { count: chosen, defaultValue: "Archive {{count}} tasks" })
        : chosen > 20
          ? t("tasks.mochi.updateN", { count: chosen, defaultValue: "Update {{count}} tasks" })
          : t("tasks.mochi.confirm", { defaultValue: "Confirm" });

  const apply = async () => {
    setBusy(true);
    try {
      const out = await mochiApi.apply(proposal.proposal_id, { ticked: ticked.flatMap((on, i) => (on ? [i] : [])), edits });
      setResult(out);
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      const n = create ? (out.created?.length ?? 0) : (out.count ?? out.updated?.length ?? 0);
      const msg = create ? t("tasks.mochi.createdN", { count: n, defaultValue: "{{count}} tasks created" }) : t("tasks.mochi.updatedN", { count: n, defaultValue: "{{count}} tasks updated" });
      if (n > 0)
        toast.success(msg, {
          action: out.undo_available
            ? {
                label: t("tasks.mochi.undo", { defaultValue: "Undo" }),
                onClick: () =>
                  void mochiApi.undo(proposal.proposal_id).then(
                    () => {
                      void qc.invalidateQueries({ queryKey: ["tasks"] });
                      toast.success(t("tasks.mochi.undone", { defaultValue: "Undone" }));
                    },
                    () => toast.error(t("tasks.mochi.undoFailed", { defaultValue: "Couldn't undo everything." })),
                  ),
              }
            : undefined,
        });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const dir = proposal.lang === "ar" ? "rtl" : "ltr";
  return (
    <section dir={dir} lang={proposal.lang} aria-label={t("tasks.mochi.preview", { defaultValue: "Mochi's preview" })} className="rounded-2xl border border-border bg-background p-3 space-y-3">
      <header className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
        <Sparkles className="size-3.5 text-primary" />
        {create ? t("tasks.mochi.newTasks", { defaultValue: "New tasks" }) : t("tasks.mochi.changes", { defaultValue: "Changes" })}
      </header>
      {proposal.questions && proposal.questions.length > 0 && !result && (
        <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5">
          {proposal.questions.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
      )}
      {create ? (
        <ul className="space-y-3">
          {(proposal.items as CreateItem[]).map((item, i) => (
            <li key={i} className={cn("flex gap-2", !ticked[i] && "opacity-50")}>
              <input type="checkbox" checked={ticked[i]} disabled={!!result} onChange={() => setTicked((x) => x.map((on, j) => (j === i ? !on : on)))} aria-label={t("tasks.mochi.include", { title: item.title, defaultValue: "Include {{title}}" })} className="mt-2 accent-[var(--accent)]" />
              <div className="flex-1 min-w-0">
                <CreateRow item={item} squads={proposal.squads ?? []} edit={edits[String(i)] ?? {}} onEdit={(p) => setEdits((e) => ({ ...e, [String(i)]: { ...e[String(i)], ...p } }))} t={t} />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground text-start">
              <th className="w-6" />
              <th className="text-start font-semibold py-1">{t("tasks.mochi.task", { defaultValue: "Task" })}</th>
              <th className="text-start font-semibold py-1">{t("tasks.mochi.change", { defaultValue: "Change" })}</th>
            </tr>
          </thead>
          <tbody>
            {(proposal.items as ChangeItem[]).map((item, i) => (
              <tr key={item.task.id} className={cn("border-t border-border align-top", !ticked[i] && "opacity-50")}>
                <td className="py-1.5">
                  <input type="checkbox" checked={ticked[i]} disabled={!!result} onChange={() => setTicked((x) => x.map((on, j) => (j === i ? !on : on)))} aria-label={t("tasks.mochi.include", { title: item.task.title, defaultValue: "Include {{title}}" })} className="accent-[var(--accent)]" />
                </td>
                <td className="py-1.5 pe-2">
                  <button type="button" onClick={() => openTask(item.task.id)} className="text-start hover:underline">
                    <span className="font-bold text-muted-foreground me-1">{item.task.key}</span>
                    {item.task.title}
                  </button>
                </td>
                <td className="py-1.5">
                  {item.rows.map((r) => (
                    <span key={r.field} className="block">
                      {proposal.action === "update" || !proposal.action ? (
                        <>
                          {fieldLabel(r.field, t)}: <span className="text-muted-foreground">{r.old ?? t("tasks.mochi.none", { defaultValue: "None" })}</span> → <span className="font-semibold">{r.new ?? t("tasks.mochi.none", { defaultValue: "None" })}</span>
                        </>
                      ) : (
                        fieldLabel(r.field, t)
                      )}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {proposal.refused && proposal.refused.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary>{t("tasks.mochi.skipped", { count: proposal.refused.length, defaultValue: "{{count}} tasks skipped" })}</summary>
          <ul className="mt-1 space-y-0.5">
            {proposal.refused.map((r) => (
              <li key={r.task.id}>
                {r.task.key} — {r.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
      {result ? (
        <div className="text-xs space-y-1" role="status">
          <p className="flex items-center gap-1 font-semibold text-green-700 dark:text-green-300">
            <Check className="size-3.5" />
            {create ? t("tasks.mochi.createdN", { count: result.created?.length ?? 0, defaultValue: "{{count}} tasks created" }) : t("tasks.mochi.updatedN", { count: result.count ?? result.updated?.length ?? 0, defaultValue: "{{count}} tasks updated" })}
          </p>
          {result.failed.map((f, i) => (
            <p key={i} className="text-red-600">
              {f.title ? `${f.title}: ` : ""}
              {f.detail}
            </p>
          ))}
        </div>
      ) : (
        <div className="flex justify-end">
          <button type="button" disabled={busy || chosen === 0 || unresolved} onClick={() => void apply()} className={cn("h-9 px-4 rounded-xl text-sm font-bold disabled:opacity-40", proposal.action === "delete" ? "bg-red-500 text-white" : "bg-primary text-primary-foreground")}>
            {confirmLabel}
          </button>
        </div>
      )}
    </section>
  );
}
